import { useSearchParams } from 'react-router-dom';
import { cn } from '../../utils/cn';

/** Read filter state from the URL so views are shareable and the back button works. */
export function readFilters(searchParams, filters) {
	const out = {};
	for (const f of filters) {
		const vals = searchParams.getAll(f.key);
		out[f.key] = f.multi ? vals : vals[0] || null;
	}
	return out;
}

export function FilterBar({ filters = [], sort, className }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const current = readFilters(searchParams, filters);
	const sortValue = searchParams.get('sort') || sort?.defaultValue;

	// Every write uses the functional form. Building from the captured
	// `searchParams` means two writes with no render between them both derive
	// from the same value, and the second discards the first. That matters here
	// because this bar and its host page both write `sort` - a single
	// interaction that touches both would otherwise drop one.
	const toggle = (f, value) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (f.multi) {
				const vals = next.getAll(f.key);
				next.delete(f.key);
				const set = vals.includes(value) ? vals.filter((v) => v !== value) : [...vals, value];
				set.forEach((v) => next.append(f.key, v));
			} else {
				if (next.get(f.key) === value) next.delete(f.key); else next.set(f.key, value);
			}
			return next;
		}, { replace: true });
	};

	const setSort = (value) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set('sort', value);
			return next;
		}, { replace: true });
	};

	return (
		<div className={cn('flex flex-wrap items-center gap-x-5 gap-y-3', className)}>
			{filters.map((f) => (
				<div key={f.key} className="flex flex-wrap items-center gap-1.5">
					<span className="mr-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{f.label}</span>
					{f.options.map((o) => {
						const active = f.multi ? current[f.key].includes(o.value) : current[f.key] === o.value;
						return (
							<button key={o.value} type="button" aria-pressed={active} onClick={() => toggle(f, o.value)}
								className={cn('rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600',
									active ? 'bg-brand-600 text-white' : 'bg-muted text-foreground hover:bg-accent')}>
								{o.label}
							</button>
						);
					})}
				</div>
			))}
			{sort && (
				<label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
					<span>Sort</span>
					<select aria-label="Sort" value={sortValue} onChange={(e) => setSort(e.target.value)}
						className="rounded-lg border border-border bg-card px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-600">
						{sort.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
					</select>
				</label>
			)}
		</div>
	);
}
export default FilterBar;
